import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  IconButton,
  Divider,
} from "@mui/material";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      role="contentinfo"
      sx={{
        bgcolor: "grey.900",
        color: "grey.50",
        pt: 8,
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Typography
              component="div"
              sx={{
                mb: 2,
                color: "white",
                fontWeight: 700,
                fontSize: "1.5rem",
              }}
            >
              Rusha
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "grey.400", maxWidth: 300 }}
            >
              Streamline your cloud deployments with templated applications.
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={4}>
              <Grid item xs={6} sm={4}>
                <Typography
                  variant="h4"
                  component="h4"
                  sx={{ mb: 3, color: "white", fontSize: "1.1rem" }}
                >
                  Product
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {[
                    { label: "Features", href: "/#features" },
                    { label: "How It Works", href: "/#how-it-works" },
                    { label: "VSCode Extension", href: "/#vscode-extension" },
                    { label: "Get Started", href: "/#get-started" },
                  ].map((item) => (
                    <Box component="li" key={item.label} sx={{ mb: 1.5 }}>
                      <Link
                        href={item.href}
                        sx={{
                          color: "grey.400",
                          fontSize: "0.9rem",
                          "&:hover": { color: "white" },
                        }}
                        underline="hover"
                      >
                        {item.label}
                      </Link>
                    </Box>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={6} sm={4}>
                <Typography
                  variant="h4"
                  component="h4"
                  sx={{ mb: 3, color: "white", fontSize: "1.1rem" }}
                >
                  Resources
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {[
                    { label: "Documentation", href: "/docs" },
                    { label: "Guides", href: "/docs" },
                    { label: "Blog", href: "/docs" },
                    { label: "FAQ", href: "/docs" },
                  ].map((item) => (
                    <Box component="li" key={item.label} sx={{ mb: 1.5 }}>
                      <Link
                        href={item.href}
                        sx={{
                          color: "grey.400",
                          fontSize: "0.9rem",
                          "&:hover": { color: "white" },
                        }}
                        underline="hover"
                      >
                        {item.label}
                      </Link>
                    </Box>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={6} sm={4}>
                <Typography
                  variant="h4"
                  component="h4"
                  sx={{ mb: 3, color: "white", fontSize: "1.1rem" }}
                >
                  Company
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {[
                    { label: "About", href: "/docs" },
                    { label: "Contact", href: "/docs" },
                    { label: "Careers", href: "/docs" },
                    { label: "Legal", href: "/docs" },
                  ].map((item) => (
                    <Box component="li" key={item.label} sx={{ mb: 1.5 }}>
                      <Link
                        href={item.href}
                        sx={{
                          color: "grey.400",
                          fontSize: "0.9rem",
                          "&:hover": { color: "white" },
                        }}
                        underline="hover"
                      >
                        {item.label}
                      </Link>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 6, mb: 4, borderColor: "grey.800" }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Typography variant="body2" sx={{ color: "grey.500" }}>
            &copy; {currentYear} Rusha. All rights reserved.
          </Typography>

          <Box>
            <IconButton
              component="a"
              href="https://github.com/Rusha-Corp"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label="Visit Rusha on GitHub"
              sx={{ color: "grey.500", "&:hover": { color: "white" } }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
            </IconButton>
            <IconButton
              component="a"
              href="https://x.com/rushadev"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label="Follow Rusha on X"
              sx={{ color: "grey.500", mx: 1, "&:hover": { color: "white" } }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </IconButton>
            <IconButton
              component="a"
              href="https://discord.gg/rusha"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label="Join Rusha on Discord"
              sx={{ color: "grey.500", "&:hover": { color: "white" } }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8449-.2762-3.68-.2762-5.4862 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0287C.5334 9.0458-.319 13.5599.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0295a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.9024a.077.077 0 01-.0076-.1262c.1258-.1094.2517-.2234.3718-.3428a.075.075 0 01.0776-.0209c3.9278 1.7933 8.18 1.7933 12.0614 0a.0754.0754 0 01.0785.0195c.1202.1193.246.2334.3728.343a.0765.0765 0 01-.0063.1265c-.598.3544-1.2206.6551-1.8733.9027a.0762.0762 0 00-.0409.1068c.3604.698.7719 1.3628 1.225 1.9932a.0767.0767 0 00.0842.0286c1.961-.6069 3.9495-1.5229 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8379-9.6739-3.5486-13.6565a.061.061 0 00-.0312-.0288zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
